import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Spin,
  Typography,
} from '@arco-design/web-react';
import {
  ApiError,
  errorMessage,
} from '../../../common/api/httpClient';
import {
  sourceActionErrorMessage,
  withSourceSaveError,
} from '../../../common/api/sourceApplicationError';
import type {
  PortalApplication,
  PortalIdentity,
} from '../../../model/portalModels';
import { ensureAccessToken } from '../../auth/accessTokenManager';
import {
  startRegisteredApplication,
  type PortalOperationPageBridge,
} from '../../../runtime/wujieLoader';
import type {
  BusinessOperationCloseRequest,
  BusinessOperationCompletionResult,
} from '@jukun/platform-sdk/auth';
import { requireFlowPageSynchronized } from '@jukun/platform-sdk/shell';
import { draftsApi } from '../../drafts/api/draftsApi';
import { operationsApi } from '../api/operationsApi';
import type { PortalOperationResponse } from '../types/portal.types';
import {
  probeOperationRoute,
  resolveOperationTargetUrl,
  type OperationFailureStage,
  waitForOperationRoute,
} from '../runtime/operationRuntime';
import { usePortalOverlay } from '../../../components/PortalOverlayProvider';

function releaseWithoutUnhandled(
  cleanup?: () => void | Promise<void>,
) {
  if (!cleanup) return;
  void Promise.resolve()
    .then(() => cleanup())
    .catch(() => undefined);
}

function isSafeBusinessValue(value: unknown, maxLength: number) {
  return typeof value === 'string'
    && Boolean(value.trim())
    && value.trim().length <= maxLength
    && !/[\r\n]/.test(value);
}

function isCurrentCompletion(
  value: unknown,
  operationInvocationId: string,
): value is BusinessOperationCompletionResult {
  if (!value || typeof value !== 'object') return false;
  const result = value as Record<string, unknown>;
  return result.operationInvocationId === operationInvocationId
    && ['SUCCEEDED', 'SUCCEEDED_WITH_WARNING'].includes(
      String(result.status),
    )
    && isSafeBusinessValue(result.businessObjectType, 160)
    && isSafeBusinessValue(result.businessObjectPublicId, 200)
    && (result.message === undefined
      || isSafeBusinessValue(result.message, 300))
    && (result.warningCode === undefined
      || (typeof result.warningCode === 'string'
        && /^[A-Z][A-Z0-9_]{2,63}$/.test(result.warningCode)))
    && (result.refreshSuggested === undefined
      || typeof result.refreshSuggested === 'boolean');
}

function isCurrentCloseRequest(
  value: unknown,
  operationInvocationId: string,
): value is BusinessOperationCloseRequest {
  if (!value || typeof value !== 'object') return false;
  return Reflect.get(value, 'operationInvocationId') === operationInvocationId;
}

export function PortalOperationHost(props: {
  applicationCode: string;
  operationCode: string;
  applications: PortalApplication[];
  identity: PortalIdentity;
  onSessionExpired: () => void;
  onClose: () => void;
}) {
  const { message, reportOperationFeedback } = usePortalOverlay();
  const containerRef = useRef<HTMLDivElement>(null);
  const [operation, setOperation] =
    useState<PortalOperationResponse>();
  const [state, setState] =
    useState<'loading' | 'mounted' | 'failed'>('loading');
  const [failureStage, setFailureStage] =
    useState<OperationFailureStage>();
  const [failureMessage, setFailureMessage] = useState<string>();
  const [revision, setRevision] = useState(0);
  const [pageBridge, setPageBridge] =
    useState<PortalOperationPageBridge>();
  const [pendingAction, setPendingAction] =
    useState<string>();
  const onCloseRef = useRef(props.onClose);
  onCloseRef.current = props.onClose;

  useEffect(() => {
    if (!containerRef.current) return undefined;
    const controller = new AbortController();
    const mountId = crypto.randomUUID();
    let active = true;
    let release: (() => void | Promise<void>) | undefined;
    let routeReady: Promise<void> | undefined;
    let lifecycleSettled = false;
    let stage: OperationFailureStage = 'detail';
    const operationInvocationId = crypto.randomUUID();
    const container = containerRef.current;
    setState('loading');
    setFailureStage(undefined);
    setFailureMessage(undefined);

    const mount = async () => {
      const current = await operationsApi.detail(
        props.applicationCode, props.operationCode,
      );
      if (!active) return;
      setOperation(current);
      if (!current.availability.available) {
        stage = 'availability';
        throw new Error(current.availability.message);
      }
      // 操作详情已按对应 FLOW/BUTTON 授权；入口元数据不依赖主导航 APPLICATION grant。
      const source = current.sourceApplication;
      if (!source.entry || source.applicationCode !== current.applicationCode) {
        stage = 'availability';
        throw new Error('来源应用入口信息不可用');
      }
      const application: PortalApplication = {
        appCode: source.applicationCode,
        appName: source.applicationName,
        status: source.applicationStatus ?? 'DISABLED',
        entry: source.entry,
      };
      stage = 'route';
      const targetUrl = resolveOperationTargetUrl(
        application, current.pageRoute,
      );
      stage = 'probe';
      const probe = await probeOperationRoute(targetUrl);
      if (!probe.ok) {
        throw new Error(
          probe.status
            ? `入口探测失败（HTTP ${probe.status}）`
            : `入口探测失败（${probe.code}）`,
        );
      }
      const token = await ensureAccessToken(
        application.appCode,
      );
      let reportReady:
        ((pageRoute: string) => void) | undefined;
      let reportFailed: Parameters<
        NonNullable<Parameters<typeof waitForOperationRoute>[0]>
      >[0]['failed'] | undefined;
      routeReady = waitForOperationRoute(
        handlers => {
          reportReady = handlers.ready;
          reportFailed = handlers.failed;
        },
        current.pageRoute,
      );
      stage = 'mount';
      release = await startRegisteredApplication({
        application,
        mountId,
        signal: controller.signal,
        runtimePurpose: 'transient',
        container,
        identity: props.identity,
        pathname:
          `${application.entry.basePath}${current.pageRoute}`,
        accessToken: token,
        refreshAccessToken: () => ensureAccessToken(
          application.appCode, true,
        ),
        registerPrimaryNavigation: () => undefined,
        navigate: () => undefined,
        onPrimaryNavigationChange: () => undefined,
        onSessionExpired: props.onSessionExpired,
        reportOperationFeedback,
        operationRuntime: {
          pageRoute: current.pageRoute,
          reportRouteReady: pageRoute =>
            reportReady?.(pageRoute),
          reportRouteFailed: failure =>
            reportFailed?.(failure),
          registerPageBridge: setPageBridge,
          operationInvocationId,
          reportCompleted: (
            result: BusinessOperationCompletionResult,
          ) => {
            if (
              !active
              || lifecycleSettled
              || !isCurrentCompletion(result, operationInvocationId)
            ) return;
            lifecycleSettled = true;
            onCloseRef.current();
            if (result.status === 'SUCCEEDED_WITH_WARNING') {
              message.warning(
                result.message ?? '业务操作已完成，但有附属事项需要处理',
              );
            } else {
              message.success(result.message ?? '业务操作已完成');
            }
          },
          requestClose: (
            request: BusinessOperationCloseRequest,
          ) => {
            if (
              !active
              || lifecycleSettled
              || !isCurrentCloseRequest(request, operationInvocationId)
            ) return;
            lifecycleSettled = true;
            onCloseRef.current();
          },
        },
      });
      stage = 'render';
      await routeReady;
      if (active) setState('mounted');
    };

    void mount().catch(error => {
      if (!active) return;
      if (error instanceof ApiError && error.status === 401) {
        props.onSessionExpired();
        return;
      }
      releaseWithoutUnhandled(release);
      release = undefined;
      void routeReady?.catch(() => undefined);
      setFailureStage(stage);
      setFailureMessage(
        error instanceof Error
          ? error.message : '操作页面加载失败',
      );
      setState('failed');
      console.error('Portal operation page failed', {
        applicationCode: props.applicationCode,
        operationCode: props.operationCode,
        pageRoute: operation?.pageRoute,
        stage,
      });
    });
    return () => {
      active = false;
      controller.abort();
      releaseWithoutUnhandled(release);
    };
  }, [
    props.applicationCode,
    props.operationCode,
    props.identity,
    props.onSessionExpired,
    message,
    reportOperationFeedback,
    revision,
  ]);

  const executeAction = async (actionCode: string) => {
    if (!pageBridge || pendingAction) return;
    setPendingAction(actionCode);
    try {
      if (actionCode === 'SAVE') {
        await withSourceSaveError(() => pageBridge.save());
        message.success('保存成功');
        return;
      }
      const saved = await withSourceSaveError(async () =>
        requireFlowPageSynchronized(await pageBridge.prepareSubmit()));
      if (saved.syncStatus !== 'SYNCED') {
        throw new Error('草稿尚未同步到中台，请稍后重试');
      }
      await draftsApi.execute(
        saved.draftPublicId,
        {
          actionCode,
          expectedDraftVersion:
            saved.coreDraftVersion,
          operationId: crypto.randomUUID(),
        },
      );
      message.success('流程已发起');
      props.onClose();
    } catch (error) {
      message.error(sourceActionErrorMessage(error));
    } finally {
      setPendingAction(undefined);
    }
  };

  const shellActions =
    operation?.sourceKind === 'FLOW'
      ? operation.flowPageContext?.availableActions ?? []
      : [];

  return (
    <div className="portal-operation-host">
      <header className="ui-detail-header portal-operation-host__header">
        <div>
          <Typography.Title heading={5}>
            {operation?.name ?? '快捷操作'}
          </Typography.Title>
          <Typography.Text type="secondary">
            {operation
              ? `${operation.sourceApplication.applicationName} · ${operation.description ?? ''}`
              : '正在解析操作入口'}
          </Typography.Text>
        </div>
        <Button onClick={props.onClose}>返回操作列表</Button>
      </header>
      {state === 'loading' ? (
        <div className="portal-operation-host__state">
          <Spin loading tip="正在检查并加载操作页面..." />
        </div>
      ) : null}
      {state === 'failed' ? (
        <div className="portal-operation-host__state" role="alert">
          <Alert
            type="error"
            title="操作页面暂不可用"
            content={`${failureMessage ?? '请稍后重试'}${failureStage ? `（阶段：${failureStage}）` : ''}`}
          />
          <div className="portal-operation-host__state-actions">
            <Button
              type="primary"
              onClick={() => setRevision(value => value + 1)}
            >
              重新加载
            </Button>
            <Button onClick={props.onClose}>返回操作列表</Button>
          </div>
        </div>
      ) : null}
      <div
        className="portal-operation-host__container"
        ref={containerRef}
        hidden={state !== 'mounted'}
        data-application-code={props.applicationCode}
        data-operation-code={props.operationCode}
      />
      {state === 'mounted' && shellActions.length > 0 ? (
        <footer className="ui-form-footer portal-operation-host__actions">
          <Typography.Text type="secondary">
            {pageBridge
              ? '表单内容由来源应用负责校验和保存'
              : '正在等待表单就绪...'}
          </Typography.Text>
          <div>
            {shellActions.map(action => (
              <Button
                key={action.actionCode}
                type={action.emphasis === 'primary'
                  ? 'primary' : 'secondary'}
                loading={pendingAction === action.actionCode}
                disabled={!pageBridge || Boolean(
                  pendingAction
                    && pendingAction !== action.actionCode,
                )}
                onClick={() =>
                  void executeAction(action.actionCode)}
              >
                {action.label}
              </Button>
            ))}
          </div>
        </footer>
      ) : null}
    </div>
  );
}
